import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import _ from 'lodash';
import { Icon } from '@seeqdev/qomponents';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import type { WorkbookStore } from '@/workbook/workbook.store';

interface WorksheetWatchersProps {
  worksheetId: string;
  compactMode: boolean;
  classes?: string;
}

interface Viewer {
  name: string;
  username: string;
  worksheetId: string;
  worksheetDisplay: WORKBOOK_DISPLAY;
}

const VIEWER_DISPLAY_RANK_MAP: Record<WORKBOOK_DISPLAY, number> = {
  [WORKBOOK_DISPLAY.EDIT]: 0,
  [WORKBOOK_DISPLAY.VIEW]: 1,
  [WORKBOOK_DISPLAY.PRESENT]: 2,
};

const MODE_MAP: Record<WORKBOOK_DISPLAY, string> = {
  [WORKBOOK_DISPLAY.EDIT]: 'JOURNAL.ENTRY.EDITING',
  [WORKBOOK_DISPLAY.VIEW]: 'JOURNAL.ENTRY.VIEWING',
  [WORKBOOK_DISPLAY.PRESENT]: 'JOURNAL.ENTRY.PRESENTING',
};

export const MAX_WATCHERS_LIST_SIZE = 10;
export const WorksheetWatchers: React.FunctionComponent<WorksheetWatchersProps> = ({
  worksheetId,
  compactMode,
  classes,
}) => {
  const { t } = useTranslation();

  // A list of viewers containing information about the users viewing the page. Elements in the list are of the form:
  // {  name: the user's name, or username if not given
  //    username: the username of the user
  //    worksheetId: the id of the worksheet their viewing
  //    worksheetDisplay: the display mode they're using to view the worksheet
  // }
  const viewers = useFluxPath<WorkbookStore, Viewer[]>(sqWorkbookStore, () => sqWorkbookStore.viewers);

  const stateParams = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.stateParams);
  const currentUser = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser);

  const [formattedViewers, setFormattedViewers] = useState<Viewer[]>([]);
  const [viewerCount, setViewerCount] = useState(0);

  useEffect(() => {
    const formatViewers = () => {
      return (
        _.chain(viewers)
          .filter(['worksheetId', worksheetId])
          .filter((user) => user.username !== currentUser.username || worksheetId !== stateParams.worksheetId)
          .clone()
          // Sort the array so that we can filter users with multiple sessions on the same worksheet with the following
          // priority: EDIT > VIEW > PRESENT. That way EDIT users with a VIEW tab of the same worksheet will only
          // see one instance of themselves in the watchers list as an EDIT user, i.e. the one with the most privileges.
          .sort(
            (a: Viewer, b: Viewer) =>
              VIEWER_DISPLAY_RANK_MAP[a.worksheetDisplay] - VIEWER_DISPLAY_RANK_MAP[b.worksheetDisplay],
          )
          .uniqBy('username')
          .map((viewer) => ({
            ...viewer,
            name: _.trim(viewer.name) || viewer.username,
          }))
          .sortBy('name')
          .thru((list) => {
            setViewerCount(list.length);
            return list.length > MAX_WATCHERS_LIST_SIZE ? list.slice(0, MAX_WATCHERS_LIST_SIZE) : list;
          })
          .value()
      );
    };
    setFormattedViewers(formatViewers());
  }, [viewers, worksheetId, stateParams.worksheetId, currentUser.username]);

  const watchersTooltip = `<span class="worksheet-watchers-tooltip">
      <p class="textAlignLeft mb0">${t('WATCHERS_HELP')}</p>
      <ul id="specWatcherList" class="pl15 textAlignLeft mt5 mb5">
        ${_.map(
          formattedViewers,
          (viewer, i) =>
            `<li key={${viewer.username}${i}}>
            ${viewer.name} (<span>${t(MODE_MAP[viewer.worksheetDisplay])}</span>)
          </li>`,
        ).join('')}
      </ul>
      ${viewerCount > MAX_WATCHERS_LIST_SIZE ? '<p class="text-center">...</p>' : ''}
    </span>`;

  return formattedViewers.length > 0 ? (
    <HoverTooltip placement="bottom" formattedText={watchersTooltip}>
      <span className={clsx('tw:whitespace-nowrap mr5 cursorPointer', classes)}>
        <Icon icon="fa-eye" type="inherit" extraClassNames={clsx({ 'fa-xlg': !compactMode })} testId="watcherEyeball" />
        <sub data-testid="watcherCount">{viewerCount}</sub>
      </span>
    </HoverTooltip>
  ) : null;
};
